import { Database, Server, Radio, Check } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Reveal, Eyebrow } from "@/components/Reveal";

const PARTNERS = [
    {
        icon: Database,
        name: "Cloudera",
        tag: "Hybrid Data Platform",
        body: "Cloudera empowers organizations to transform complex data into actionable insight through a secure, scalable, open data platform. With the Cloudera Data Platform (CDP), enterprises manage and analyze data across hybrid and multi-cloud environments with unified security, governance and control, supporting data engineering, AI/ML development and real-time analytics at scale.",
        together: ["CDP deployment & migration programs", "Data engineering accelerators on lakehouse architecture", "Governance, lineage & security hardening"],
    },
    {
        icon: Server,
        name: "Hewlett Packard Enterprise",
        tag: "HPC & Supercomputing",
        body: "HPE is a global leader in high-performance computing, delivering cutting-edge systems that solve complex scientific, engineering and data-intensive challenges. HPE HPC combines powerful compute, advanced interconnects and scalable storage to support AI training, machine learning, climate modeling, genomics and more, from HPE Cray supercomputers to a robust HPC software stack.",
        together: ["HPC cluster design, sizing & rollout", "AI training infrastructure on HPE Cray", "Hybrid infrastructure operations & support"],
    },
    {
        icon: Radio,
        name: "Confluent",
        tag: "Real-Time Data Streaming",
        body: "Built on Apache Kafka, Confluent is the leading data streaming platform, enabling real-time data movement and processing across cloud and on-premises environments. Organizations connect applications, systems and data sources to power event-driven architectures, from real-time analytics to AI/ML integration, on a fully managed, secure and scalable platform.",
        together: ["Kafka platform setup & managed operations", "Streaming application development", "Real-time analytics & AI enablement"],
    },
];

const BADGES = ["NVIDIA", "AWS", "Microsoft Azure", "Google Cloud", "Red Hat", "Kubernetes"];

export default function PartnersPage() {
    return (
        <PageShell
            eyebrow="Partners & Vendors"
            title="World-class platforms,"
            accent="integrated by Syntera."
            sub="We partner with the technology leaders behind the modern data stack, so your solutions stand on proven, enterprise-grade foundations."
            testid="partners-page"
        >
            <section className="py-20 sm:py-28">
                <div className="mx-auto max-w-5xl space-y-6 px-5 sm:px-8">
                    {PARTNERS.map((p, i) => (
                        <Reveal key={p.name} delay={0.05}>
                            <div data-testid={`partner-block-${i}`} className="card-glow-border rounded-2xl border border-white/[0.08] bg-card2/50 p-8 sm:p-10">
                                <div className="flex flex-col gap-6 sm:flex-row">
                                    <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt">
                                        <p.icon size={26} />
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt">{p.tag}</div>
                                        <h2 className="mb-3 mt-1 font-syne text-xl font-semibold text-white sm:text-2xl">{p.name}</h2>
                                        <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{p.body}</p>
                                        <div className="mt-6">
                                            <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                What we deliver together
                                            </div>
                                            <ul className="grid gap-2.5 sm:grid-cols-2">
                                                {p.together.map((t) => (
                                                    <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                                                        <span className="mt-0.5 flex shrink-0 items-center justify-center rounded-full bg-volt/15 p-0.5">
                                                            <Check size={11} className="text-volt" />
                                                        </span>
                                                        {t}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-t border-white/[0.06] bg-panel/40 py-20 sm:py-24" data-testid="partners-ecosystem">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>Integration Ecosystem</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="partners-ecosystem-heading">
                            Certified across the stack
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-8 flex flex-wrap gap-3" data-testid="partners-badges">
                            {BADGES.map((b) => (
                                <span
                                    key={b}
                                    className="rounded-full border border-white/10 bg-white/[0.03] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300 transition-colors duration-300 hover:border-volt/40 hover:text-volt"
                                >
                                    {b}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>
        </PageShell>
    );
}
